import {shade, rgba} from "polished"
import maxWidthMedia, {minWidthMedia} from "./media"

// Colors that are manipulated / used multiple times in theme
export const perkboxBlue = "#0496FF"
export const SHADE_MODIFIER = 0.8
export const HOVER_MODIFIER = 0.5
export const DARK_MODIFIER = 0.2
export const SHADOW_MODIFIER = 0.17

// TODO move this object inline in the theme once all the duplicate values have been normalised (eg: mainTheme.fontColor)
const greyscale = {
  white: "#FFFFFF",
  grey03: "#F4F4F7",
  grey12: "#D7D6DA",
  grey20: "#C7C7CC",
  grey50: "#777680",
  black: "#000000"
}

const dropShadowGrey = rgba(greyscale.grey50, SHADOW_MODIFIER)

const mainTheme = {
  colorFunctions: {
    // @todo: remove this + change usage in `apps/web/tour/src/components/styled.ts` to use pallet colours
    rgba
  },
  colors: {
    success: "#00BDB3",
    error: "#B30000",
    fontColor: greyscale.black,
    // TODO change name, put this name as placeholder as only component using it until design/ FE agreed upon
    filterChip: "#2F3037",
    tooltip: "#1EBCB2",
    brand: {
      default: perkboxBlue,
      shade: `${shade(SHADE_MODIFIER, perkboxBlue)}`,
      dark: `${shade(DARK_MODIFIER, perkboxBlue)}`,
      hover: `${shade(HOVER_MODIFIER, perkboxBlue)}`
    },
    perkbox: {
      blue: perkboxBlue,
      darkBlue: "#00678E",
      greenBlue: "#21A0A0"
    },
    products: {
      medical: "#1EBCB2",
      perks: perkboxBlue,
      recognition: "#EE6352",
      wellness: "#8352E1",
      rewards: "#E4A700"
    },
    greys: {
      lightest: greyscale.grey03,
      graphGrey: greyscale.grey03,
      light: greyscale.grey12,
      mediumLight: greyscale.grey20,
      medium: greyscale.grey20,
      mediumDark: greyscale.grey50,
      dark: greyscale.grey50,
      disabled: greyscale.grey12,
      disabledText: greyscale.grey50,
      dropShadowGrey,
      ...greyscale
    },

    // ComponentLibrary Assets below
    primary1_600: "#007AD2",
    primary1: "#0496FF",
    primary1_200: "#81CAFF",
    primary1_100: "#B3DFFF",
    primary1_50: "#DAEFFF",
    primary2: "#21295C",
    primary2_400: "#545B8D",
    primary2_200: "#9094AD",
    primary2_100: "#BCBECE",
    primary2_50: "#DEDFE7",
    secondary1: "#3DCCC7",
    secondary1_200: "#9EE5E3",
    secondary1_100: "#C4EFEE",
    secondary1_50: "#E2F8F7",
    secondary2: "#028090",
    secondary2_200: "#80BFC7",
    secondary2_100: "#B3D8DD",
    secondary2_50: "#D9ECEF",
    tertiary: {
      g_400: "#00BD9D",
      g_200: "#73D3C4",
      g_600: "#15837F",
      b_400: "#1F59D5",
      b_300: "#5787F5",
      b_200: "#88A7E5",
      b_50: "#D4E1F6",
      r_400: "#DE3434",
      r_200: "#E89495",
      y_400: "#F9BC14",
      y_200: "#F6D885",
      o_400: "#F97814",
      o_200: "#F6B685",
      o_50: "#FEF8F3",
      p_400: "#FF4270",
      p_200: "#F99BB3",
      v_400: "#41AECF",
      v_200: "#8FCEE0",
      j_400: "#52489C",
      j_200: "#AAA6CE",
      i_400: "#8352E1",
      i_200: "#BBA3EC",
      i_50: "#E8E1F6",
      f_400: "#C140AF",
      f_200: "#D99AD2",
      f_50: "#F5E6F4",
      s2_50: "#D9ECEF",
      s2_400: "#028090"
    },
    utility: {
      white: greyscale.white,
      ink: greyscale.black,
      subdued: greyscale.grey20,
      contrast: greyscale.black,
      highlight: greyscale.grey03,
      acid: "#BDF168",
      success: "#00BDB3",
      bgSuccess: "#D1E6DD",
      warning: "#D49900",
      bgWarning: "#FFF2CD",
      error: "#B30000",
      bgError: "#F8D7D9",
      bgInfo: "#CEF4FC",
      gold: "#E3AE5D",
      silver: "#CAC7C3",
      bronze: "#AE956F"
    }
  },
  spacing: {
    stepSize: 8,
    half: "4px",
    one: "8px",
    oneAndHalf: "12px",
    two: "16px",
    three: "24px",
    four: "32px",
    five: "40px",
    six: "48px",
    seven: "56px",
    eight: "64px",
    nine: "72px",
    ten: "80px",
    eleven: "88px",
    twelve: "96px"
  },
  font: {
    light: "WorkSansLight, sans-serif",
    default: "WorkSans, sans-serif",
    medium: "WorkSansMedium, sans-serif",
    semibold: "WorkSansSemibold, sans-serif",
    bold: "WorkSansBold, sans-serif"
  },
  border: {
    radius: {
      small: "2px",
      medium: "4px",
      large: "8px",
      extraLarge: "12px"
    }
  },
  shadows: {
    low: "1px 2px 5px 0 rgba(0,0,0,0.1)",
    mid: "1px 3px 10px 0 rgba(0,0,0,0.1)",
    high: "1px 3px 20px 0 rgba(0,0,0,0.1)",
    focus: "0 0 4px 0 rgba(4,150,255,0.8)",
    above: {
      low: "-1px -2px 5px 0 rgba(0,0,0,0.1)",
      mid: "-1px -3px 10px 0 rgba(0,0,0,0.1)",
      high: "-1px -3px 20px 0 rgba(0,0,0,0.1)"
    },
    inset: {
      low: "inset 1px 2px 5px 0 rgba(0,0,0,0.1)",
      mid: "inset 1px 3px 10px 0 rgba(0,0,0,0.1)",
      high: "inset 1px 3px 20px 0 rgba(0,0,0,0.1)"
    }
  },
  mediaQueries: {
    maxWidthMedia,
    minWidthMedia
  }
}

export default mainTheme
